import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { Check, X, ArrowLeft, Loader2, BookOpen, Clock, IndianRupee, Baby } from "lucide-react";

export default function CurriculumDetail() {
  const { id } = useParams();
  const [c, setC] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => { api.get(`/curricula/${id}`).then((r) => setC(r.data)).catch(() => setErr("Not found")); }, [id]);

  if (err) return <div className="text-center py-24"><h1 className="text-2xl text-brand-blue">Curriculum not found</h1><Link to="/curriculum" className="btn-primary mt-6">Back to explorer</Link></div>;
  if (!c) return <div className="text-center py-24"><Loader2 className="animate-spin text-brand-green mx-auto" /></div>;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <Link to="/curriculum" className="btn-ghost mb-6"><ArrowLeft size={16} /> Back to explorer</Link>
      <div className="chip mb-3">{c.category} • {c.region}</div>
      <h1 className="text-4xl sm:text-5xl font-semibold text-brand-blue">{c.name}</h1>
      <p className="mt-4 text-lg text-brand-muted leading-relaxed max-w-3xl">{c.description}</p>

      <div className="mt-8 grid sm:grid-cols-4 gap-4">
        {[
          { icon: Baby, label: "Age range", value: c.age_range },
          { icon: Clock, label: "Method", value: c.method },
          { icon: IndianRupee, label: "Cost", value: c.cost },
          { icon: BookOpen, label: "Resources", value: (c.resources || []).length + " listed" },
        ].map((s, i) => (
          <div key={i} className="surface p-5">
            <s.icon size={20} className="text-brand-green mb-2" />
            <div className="text-xs uppercase tracking-widest text-brand-muted">{s.label}</div>
            <div className="font-heading font-medium text-brand-blue mt-1">{s.value}</div>
          </div>
        ))}
      </div>

      <div className="mt-8 grid md:grid-cols-2 gap-6">
        <div className="surface p-7">
          <div className="chip mb-3">Pros</div>
          <ul className="space-y-2.5">
            {(c.pros || []).map((p, i) => <li key={i} className="flex gap-2"><Check size={18} className="text-brand-green shrink-0 mt-0.5" /><span className="text-brand-blue">{p}</span></li>)}
          </ul>
        </div>
        <div className="surface p-7">
          <div className="chip-orange mb-3">Cons</div>
          <ul className="space-y-2.5">
            {(c.cons || []).map((p, i) => <li key={i} className="flex gap-2"><X size={18} className="text-brand-orange shrink-0 mt-0.5" /><span className="text-brand-blue">{p}</span></li>)}
          </ul>
        </div>
      </div>

      <div className="surface p-7 mt-6">
        <h3 className="text-xl font-semibold text-brand-blue mb-3">Recommended resources</h3>
        <div className="flex flex-wrap gap-2">
          {(c.resources || []).map((r) => <span key={r} className="chip-blue">{r}</span>)}
        </div>
      </div>

      {/* Recommended learning videos */}
      {c.videos?.length > 0 && (
        <div className="surface p-7 mt-6">
          <h3 className="text-xl font-semibold text-brand-blue mb-4">Recommended learning videos</h3>
          <div className="grid md:grid-cols-2 gap-4">
            {c.videos.map((v, i) => (
              <a key={i} href={v.url} target="_blank" rel="noopener noreferrer"
                 data-testid={`curriculum-video-${i}`}
                 className="p-4 rounded-2xl border border-brand-border hover:border-brand-blue transition-colors">
                <div className="font-heading font-semibold text-brand-blue">{v.title}</div>
                <div className="text-xs text-brand-muted mt-1">{v.channel} · {v.duration}</div>
                <div className="mt-2 text-sm text-brand-muted">{v.description}</div>
                <div className="mt-2 text-xs text-brand-green font-medium">Open on YouTube →</div>
              </a>
            ))}
          </div>
          <p className="mt-4 text-xs text-brand-muted">
            Links open public YouTube searches — we don't host or embed copyrighted content.
            Please respect each channel's terms of use.
          </p>
        </div>
      )}

      <div className="rounded-3xl p-8 mt-10 text-center bg-gradient-to-br from-brand-blue to-brand-blue-hover text-white">
        <h3 className="text-2xl font-semibold">Is {c.name} right for your child?</h3>
        <p className="mt-2 text-white/80">Take the assessment for a personalized recommendation.</p>
        <Link to="/assessment" className="btn-primary mt-5 inline-flex">Start assessment</Link>
      </div>
    </div>
  );
}
